import React from "react";
import { useTranslation } from "react-i18next";
import { observer } from "mobx-react-lite";
import { useFeatureFlag } from "../../hooks/useFeatureFlag";
import {
  CatalogImageOwner,
  CatalogListVariables,
  useFloorplannerCategoriesMeta,
  useFloorplannerProduct3DAssets,
  useFloorplannerProductBrands,
  useFloorplannerProducts,
  useFloorplannerSurfaces,
} from "../../features/floorplan3d/catalog/catalogQueries";
import { getCatalogItemDisplayName } from "../../features/floorplan3d/catalog/catalogDisplay";
import {
  findFlooringCategoryIds,
  findWallpaperCategoryIds,
  listCategoryMetaChildren,
} from "../../features/floorplan3d/catalog/wallpaperCategory";
import { collectUsedWallpapersFromPlan } from "../../features/floorplan3d/catalog/collectUsedWallpapersFromPlan";
import {
  usedWallpaperToCatalogOwner,
  type UsedWallpaperOption,
} from "../../features/floorplan3d/catalog/collectUsedWallpapers";
import {
  getWallFloorProductTextureUrl,
  listWallFloorProductTextureImageOptions,
  WallFloorProductTextureImageOption,
} from "../../features/floorplan3d/materials/textureCache";
export type ProductsPanelIntent =
  | "wallMaterial"
  | "floorMaterial"
  | "areaFillMaterial"
  | "product"
  | "wallpaperProduct"
  | "flooringProduct";

export type ProductSelectOptions = {
  /** Raw or apply-ready URL chosen in the texture picker (wall/floor products). */
  textureImageUrl?: string;
};

type SurfaceProductSort = NonNullable<CatalogListVariables["sortBy"]>;

type SurfaceProductBrowseFilters = {
  search: string;
  sortBy: SurfaceProductSort;
  selectedBrands: string[];
  colorIds: string[];
  patternIds: string[];
};

type SurfaceProductIntent = "wallpaperProduct" | "flooringProduct";

/**
 * Session-persistent wallpaper/flooring browse filters (like PaintsMenu2's
 * productFilterStore) so closing the panel does not wipe the active filters.
 */
const persistedSurfaceProductFilters: Partial<
  Record<SurfaceProductIntent, SurfaceProductBrowseFilters>
> = {};

const DEFAULT_SURFACE_PRODUCT_FILTERS: SurfaceProductBrowseFilters = {
  search: "",
  sortBy: "name",
  selectedBrands: [],
  colorIds: [],
  patternIds: [],
};

interface DropdownProps {
  position?: { top: number; left: number };
  onClose: () => void;
  dropdownRef?: React.Ref<HTMLDivElement>;
  intent?: ProductsPanelIntent;
  initialSearch?: string;
  /**
   * Open directly on the texture-image step for this product
   * (e.g. property-panel preview cards).
   */
  initialTexturePickProduct?: CatalogImageOwner | null;
  /**
   * When true, render as a fill-parent body (no fixed chrome) for embedding
   * inside the BONI "Show more" materials drawer.
   */
  embedded?: boolean;
  proxyResolver?: (product: CatalogImageOwner) => "primitiveProxy" | "billboardPreview";
  onSurfaceSelect?: (surface: CatalogImageOwner) => void;
  onProductSelect?: (
    product: CatalogImageOwner,
    options?: ProductSelectOptions,
  ) => void;
}

const RESULT_LIMIT = 24;

const SURFACE_PRODUCT_SORT_OPTIONS: Array<{
  value: SurfaceProductSort;
  labelKey: string;
}> = [
  { value: "name", labelKey: "common.name" },
  { value: "updatedAt", labelKey: "common.newest" },
  { value: "priceLow", labelKey: "productLibrary.priceLowHigh" },
  { value: "priceHigh", labelKey: "productLibrary.priceHighLow" },
];

/** Wallpaper / flooring browse thumbnails: MATERIAL bake when present, else images[0]. */
function resolveSurfaceProductThumbUrl(
  item: CatalogImageOwner,
): string | undefined {
  return getWallFloorProductTextureUrl(item);
}

function toggleId(selected: string[], id: string): string[] {
  return selected.includes(id)
    ? selected.filter((value) => value !== id)
    : [...selected, id];
}

function isSurfaceProductIntent(
  intent: ProductsPanelIntent,
): intent is SurfaceProductIntent {
  return intent === "wallpaperProduct" || intent === "flooringProduct";
}

function readPersistedFilters(
  intent: SurfaceProductIntent,
  initialSearch: string,
): SurfaceProductBrowseFilters {
  const saved = persistedSurfaceProductFilters[intent];
  if (!saved) {
    return {
      ...DEFAULT_SURFACE_PRODUCT_FILTERS,
      search: initialSearch,
    };
  }
  return {
    ...saved,
    // Explicit initialSearch (e.g. from property panel) wins once.
    search: initialSearch || saved.search,
  };
}

function textureOptionLabel(
  option: WallFloorProductTextureImageOption,
  t: (key: string, options?: Record<string, unknown>) => string,
): string {
  if (option.isMaterialBake) return t("common.aiBakedTexture");
  if (option.aiAngle) {
    return t("common.aiAngleImage", { angle: option.aiAngle });
  }
  if (option.catalogIndex != null) {
    return t("common.catalogImageN", { n: option.catalogIndex + 1 });
  }
  return t("common.additionalImage");
}

export const ProductsPanel: React.FC<DropdownProps> = observer(({
  position,
  onClose,
  dropdownRef,
  intent = "product",
  initialSearch = "",
  initialTexturePickProduct = null,
  embedded = false,
  proxyResolver,
  onSurfaceSelect,
  onProductSelect,
}) => {
  const { t } = useTranslation();
  const productAssetsEnabled = useFeatureFlag("floorplannerProduct3DAssets");

  const isWallpaperIntent = intent === "wallpaperProduct";
  const isFlooringIntent = intent === "flooringProduct";
  const isSurfaceProductBrowse = isSurfaceProductIntent(intent);
  const isSurfaceIntent =
    intent === "wallMaterial" ||
    intent === "floorMaterial" ||
    intent === "areaFillMaterial";
  const isProductBrowse = intent === "product" || isSurfaceProductBrowse;

  const persisted = isSurfaceProductBrowse
    ? readPersistedFilters(intent, initialSearch)
    : null;

  const [search, setSearch] = React.useState(
    persisted?.search ?? initialSearch,
  );
  /** Sort / brand / color / pattern only apply to wallpaper & flooring product browse. */
  const [sortBy, setSortBy] = React.useState<SurfaceProductSort>(
    persisted?.sortBy ?? "name",
  );
  const [selectedBrands, setSelectedBrands] = React.useState<string[]>(
    persisted?.selectedBrands ?? [],
  );
  const [selectedColorIds, setSelectedColorIds] = React.useState<string[]>(
    persisted?.colorIds ?? [],
  );
  const [selectedPatternIds, setSelectedPatternIds] = React.useState<string[]>(
    persisted?.patternIds ?? [],
  );
  const [openFilterMenu, setOpenFilterMenu] = React.useState<
    "brand" | "color" | "pattern" | null
  >(null);
  const filterMenusRef = React.useRef<HTMLDivElement>(null);
  const [loadingMore, setLoadingMore] = React.useState(false);
  /**
   * Wallpaper / flooring: product chosen for texture image pick.
   * Thumbs for that product load only while this is set (not in the browse grid).
   */
  const [texturePickProduct, setTexturePickProduct] =
    React.useState<CatalogImageOwner | null>(
      initialTexturePickProduct ?? null,
    );

  React.useEffect(() => {
    if (initialTexturePickProduct) {
      setTexturePickProduct(initialTexturePickProduct);
    }
  }, [initialTexturePickProduct]);

  // Persist wallpaper/flooring filters for the session (survive panel close).
  React.useEffect(() => {
    if (!isSurfaceProductBrowse) return;
    persistedSurfaceProductFilters[intent] = {
      search,
      sortBy,
      selectedBrands,
      colorIds: selectedColorIds,
      patternIds: selectedPatternIds,
    };
  }, [
    intent,
    isSurfaceProductBrowse,
    search,
    sortBy,
    selectedBrands,
    selectedColorIds,
    selectedPatternIds,
  ]);

  const categoriesQuery = useFloorplannerCategoriesMeta(!isSurfaceProductBrowse);
  const wallpaperCategoryIds = React.useMemo(
    () => findWallpaperCategoryIds(categoriesQuery.data?.categoriesMeta),
    [categoriesQuery.data?.categoriesMeta],
  );
  const flooringCategoryIds = React.useMemo(
    () => findFlooringCategoryIds(categoriesQuery.data?.categoriesMeta),
    [categoriesQuery.data?.categoriesMeta],
  );
  const surfaceProductCategoryIds = isWallpaperIntent
    ? wallpaperCategoryIds
    : isFlooringIntent
      ? flooringCategoryIds
      : [];
  const surfaceProductCategoriesReady =
    !isSurfaceProductBrowse ||
    (!categoriesQuery.loading && surfaceProductCategoryIds.length > 0);

  const colorOptions = React.useMemo(
    () =>
      listCategoryMetaChildren(
        categoriesQuery.data?.categoriesMeta,
        "Color",
      ),
    [categoriesQuery.data?.categoriesMeta],
  );
  const patternOptions = React.useMemo(
    () =>
      listCategoryMetaChildren(
        categoriesQuery.data?.categoriesMeta,
        "Pattern",
      ),
    [categoriesQuery.data?.categoriesMeta],
  );

  const brandsQuery = useFloorplannerProductBrands(
    surfaceProductCategoryIds,
    !isSurfaceProductBrowse || !surfaceProductCategoriesReady,
  );
  const brandOptions = React.useMemo(() => {
    const rows = (brandsQuery.data?.productBrands ?? []) as Array<{
      id?: string | null;
      name?: string | null;
    }>;
    return rows
      .map((row) => (row.name ?? "").trim())
      .filter(Boolean)
      .sort((a, b) => a.localeCompare(b, undefined, { sensitivity: "base" }));
  }, [brandsQuery.data?.productBrands]);

  React.useEffect(() => {
    if (!openFilterMenu) return;
    const onPointerDown = (event: MouseEvent) => {
      if (
        filterMenusRef.current &&
        !filterMenusRef.current.contains(event.target as Node)
      ) {
        setOpenFilterMenu(null);
      }
    };
    document.addEventListener("mousedown", onPointerDown);
    return () => document.removeEventListener("mousedown", onPointerDown);
  }, [openFilterMenu]);

  // Drop selected brands that disappeared after category resolve (e.g. intent switch).
  React.useEffect(() => {
    if (!isSurfaceProductBrowse || brandOptions.length === 0) return;
    setSelectedBrands((current) =>
      current.filter((brand) => brandOptions.includes(brand)),
    );
  }, [isSurfaceProductBrowse, brandOptions]);

  // Drop color/pattern ids that are no longer in the catalog tree.
  React.useEffect(() => {
    if (!isSurfaceProductBrowse) return;
    const colorIds = new Set(colorOptions.map((option) => option.id));
    const patternIds = new Set(patternOptions.map((option) => option.id));
    if (colorIds.size > 0) {
      setSelectedColorIds((current) =>
        current.filter((id) => colorIds.has(id)),
      );
    }
    if (patternIds.size > 0) {
      setSelectedPatternIds((current) =>
        current.filter((id) => patternIds.has(id)),
      );
    }
  }, [isSurfaceProductBrowse, colorOptions, patternOptions]);

  const variables = React.useMemo(
    () => ({
      limit: RESULT_LIMIT,
      skip: 0,
      search: search.trim() || undefined,
      categoryIds: isSurfaceProductBrowse
        ? surfaceProductCategoryIds.length > 0
          ? surfaceProductCategoryIds
          : undefined
        : undefined,
      colorIds:
        isSurfaceProductBrowse && selectedColorIds.length > 0
          ? selectedColorIds
          : undefined,
      patternIds:
        isWallpaperIntent && selectedPatternIds.length > 0
          ? selectedPatternIds
          : undefined,
      brands:
        isSurfaceProductBrowse && selectedBrands.length > 0
          ? selectedBrands
          : undefined,
      sortBy: isSurfaceProductBrowse ? sortBy : undefined,
    }),
    [
      search,
      isSurfaceProductBrowse,
      isWallpaperIntent,
      surfaceProductCategoryIds,
      selectedBrands,
      selectedColorIds,
      selectedPatternIds,
      sortBy,
    ],
  );
  const surfaceQuery = useFloorplannerSurfaces(variables, !isSurfaceIntent);
  const productQuery = useFloorplannerProducts(
    variables,
    !isProductBrowse || (isSurfaceProductBrowse && !surfaceProductCategoriesReady),
  );
  const loading =
    (isSurfaceProductBrowse && categoriesQuery.loading) ||
    (isSurfaceIntent ? surfaceQuery.loading : productQuery.loading);
  const items = (isSurfaceIntent
    ? surfaceQuery.data?.surfaces?.data
    : productQuery.data?.productsMeta?.data) ?? [];
  const totalCount = isSurfaceIntent
    ? (surfaceQuery.data?.surfaces?.total as number | undefined) ?? 0
    : (productQuery.data?.productsMeta?.total as number | undefined) ?? 0;
  const hasMore = items.length < totalCount;
  const productIds = React.useMemo(
    () => (isSurfaceIntent ? [] : items.map((item: CatalogImageOwner) => item.id)),
    [isSurfaceIntent, items],
  );

  const loadMore = React.useCallback(async () => {
    if (loadingMore || !hasMore) return;
    setLoadingMore(true);
    try {
      if (isSurfaceIntent) {
        await surfaceQuery.fetchMore({
          variables: { ...variables, skip: items.length },
          updateQuery: (previous, { fetchMoreResult }) => {
            if (!fetchMoreResult?.surfaces) return previous;
            const prevData = previous.surfaces?.data ?? [];
            const nextData = fetchMoreResult.surfaces.data ?? [];
            const seen = new Set(prevData.map((row: { id: string }) => row.id));
            return {
              ...previous,
              surfaces: {
                ...fetchMoreResult.surfaces,
                data: [
                  ...prevData,
                  ...nextData.filter(
                    (row: { id: string }) => !seen.has(row.id),
                  ),
                ],
              },
            };
          },
        });
      } else {
        await productQuery.fetchMore({
          variables: { ...variables, skip: items.length },
          updateQuery: (previous, { fetchMoreResult }) => {
            if (!fetchMoreResult?.productsMeta) return previous;
            const prevData = previous.productsMeta?.data ?? [];
            const nextData = fetchMoreResult.productsMeta.data ?? [];
            const seen = new Set(prevData.map((row: { id: string }) => row.id));
            return {
              ...previous,
              productsMeta: {
                ...fetchMoreResult.productsMeta,
                data: [
                  ...prevData,
                  ...nextData.filter(
                    (row: { id: string }) => !seen.has(row.id),
                  ),
                ],
              },
            };
          },
        });
      }
    } finally {
      setLoadingMore(false);
    }
  }, [
    hasMore,
    isSurfaceIntent,
    items.length,
    loadingMore,
    productQuery,
    surfaceQuery,
    variables,
  ]);
  // Wallpaper / flooring products do not need 3D asset badges / fetches.
  const assetQuery = useFloorplannerProduct3DAssets(
    productIds,
    isSurfaceIntent || isSurfaceProductBrowse || !productAssetsEnabled,
  );
  const assetByProductId = React.useMemo(() => {
    const map = new Map<string, unknown>();
    (assetQuery.data?.product3DAssets ?? []).forEach((asset: any) => {
      if (!map.has(asset.productId)) map.set(asset.productId, asset);
    });
    return map;
  }, [assetQuery.data]);

  const texturePickOptions = texturePickProduct
    ? listWallFloorProductTextureImageOptions(texturePickProduct)
    : [];
  const texturePickLabel = texturePickProduct
    ? getCatalogItemDisplayName(texturePickProduct)
    : "";

  const commitProductSelect = (
    product: CatalogImageOwner,
    options?: ProductSelectOptions,
  ) => {
    const asset = assetByProductId.get(product.id);
    onProductSelect?.(
      {
        ...product,
        product3DAssets: asset ? [asset as any] : undefined,
      },
      options,
    );
  };

  const beginOrCommitSurfaceProduct = (product: CatalogImageOwner) => {
    const options = listWallFloorProductTextureImageOptions(product);
    if (options.length <= 1) {
      commitProductSelect(
        product,
        options[0] ? { textureImageUrl: options[0].rawUrl } : undefined,
      );
      return;
    }
    setTexturePickProduct(product);
  };

  const title = texturePickProduct
    ? t("common.chooseTextureImage")
    : isWallpaperIntent
      ? t("common.browseWallpaper")
      : isFlooringIntent
        ? t("common.browseFlooring")
        : isSurfaceIntent
          ? t("common.browseSurfaces")
          : t("common.browseProducts");
  const searchPlaceholder = isWallpaperIntent
    ? t("common.searchWallpaper")
    : isFlooringIntent
      ? t("common.searchFlooring")
      : isSurfaceIntent
        ? t("common.searchSurfaces")
        : t("common.searchCatalogProducts");
  const emptyMessage =
    isSurfaceProductBrowse &&
    !categoriesQuery.loading &&
    surfaceProductCategoryIds.length === 0
      ? isWallpaperIntent
        ? t("common.wallpaperCategoryMissing")
        : t("common.flooringCategoryMissing")
      : t("common.noResults");

  const hasActiveSurfaceFilters =
    selectedBrands.length > 0 ||
    selectedColorIds.length > 0 ||
    selectedPatternIds.length > 0;

  /**
   * Pinned “used in plan” wallpapers — live MobX scan (observer re-renders).
   * Hidden while searching or filtering so results stay focused.
   */
  const pinnedWallpapers =
    isWallpaperIntent && !texturePickProduct
      ? collectUsedWallpapersFromPlan()
      : [];
  const showPinnedWallpapers =
    pinnedWallpapers.length > 0 &&
    search.trim().length === 0 &&
    !hasActiveSurfaceFilters;

  const clearSurfaceFilters = () => {
    setSelectedBrands([]);
    setSelectedColorIds([]);
    setSelectedPatternIds([]);
    setOpenFilterMenu(null);
  };

  const applyPinnedWallpaper = (wallpaper: UsedWallpaperOption) => {
    commitProductSelect(
      usedWallpaperToCatalogOwner(wallpaper),
      wallpaper.imageUrl
        ? { textureImageUrl: wallpaper.imageUrl }
        : undefined,
    );
  };

  return (
    <div
      className={
        embedded
          ? "flex min-h-0 flex-1 flex-col overflow-hidden bg-white"
          : "fixed z-50 flex max-h-[72vh] min-h-80 w-[360px] flex-col overflow-hidden rounded border border-gray-300 bg-white shadow-lg"
      }
      style={
        embedded
          ? undefined
          : {
              top: position?.top ?? 96,
              right: position ? undefined : 252,
              left: position?.left,
            }
      }
      ref={dropdownRef}
    >
      {!embedded && (
        <div className="flex h-11 items-center justify-between border-b border-gray-300 px-3">
          <div className="flex min-w-0 items-center gap-2">
            {texturePickProduct && (
              <button
                type="button"
                className="shrink-0 rounded-sm px-1.5 py-0.5 text-xs text-gray-600 hover:bg-gray-100"
                onClick={() => setTexturePickProduct(null)}
              >
                ← {t("common.back")}
              </button>
            )}
            <div className="truncate text-sm font-medium">{title}</div>
          </div>
          <button
            type="button"
            className="h-8 w-8 shrink-0 rounded-sm text-lg leading-none text-gray-500 hover:bg-gray-100"
            onClick={onClose}
            aria-label={t("panels.closeCatalogBrowser")}
          >
            ×
          </button>
        </div>
      )}
      {embedded && texturePickProduct && (
        <div className="flex items-center gap-2 border-b border-gray-200 px-3 py-2">
          <button
            type="button"
            className="shrink-0 rounded-sm px-1.5 py-0.5 text-xs text-gray-600 hover:bg-gray-100"
            onClick={() => setTexturePickProduct(null)}
          >
            ← {t("common.back")}
          </button>
          <div className="truncate text-sm font-medium">{title}</div>
        </div>
      )}
      {texturePickProduct ? (
        <>
          <div className="border-b border-gray-200 px-3 py-2">
            <p className="truncate text-xs font-medium text-gray-900">
              {texturePickLabel}
            </p>
            <p className="text-[11px] text-gray-500">
              {t("common.chooseTextureImageHint")}
            </p>
          </div>
          <div className="min-h-0 flex-1 overflow-y-auto p-3">
            {texturePickOptions.length === 0 ? (
              <div className="py-8 text-center text-sm text-gray-500">
                {t("common.noImage")}
              </div>
            ) : (
              <div className="grid grid-cols-2 gap-2">
                {texturePickOptions.map((option) => (
                  <button
                    key={option.id}
                    type="button"
                    className="group overflow-hidden rounded-sm border border-gray-200 bg-white text-left hover:border-boniYellowPrimary"
                    onClick={() => {
                      if (!texturePickProduct) return;
                      commitProductSelect(texturePickProduct, {
                        textureImageUrl: option.rawUrl,
                      });
                      setTexturePickProduct(null);
                    }}
                  >
                    <div className="relative flex aspect-square items-center justify-center bg-gray-100">
                      <img
                        src={option.thumbUrl}
                        alt=""
                        className="h-full w-full object-cover"
                        loading="lazy"
                      />
                    </div>
                    <div className="px-2 py-1.5">
                      <div className="truncate text-xs font-medium text-gray-900">
                        {textureOptionLabel(option, t)}
                      </div>
                    </div>
                  </button>
                ))}
              </div>
            )}
          </div>
        </>
      ) : (
        <>
          <div className="space-y-2 border-b border-gray-200 p-3">
            <input
              className="h-9 w-full rounded-sm border border-gray-300 px-2 text-sm outline-none focus:border-blue-600"
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              placeholder={searchPlaceholder}
            />
            {isSurfaceProductBrowse && (
              <>
                <label className="flex min-w-0 items-center gap-1.5 text-[11px] text-gray-500">
                  <span className="shrink-0">{t("common.sort")}</span>
                  <select
                    className="h-8 min-w-0 flex-1 rounded-sm border border-gray-300 bg-white px-1.5 text-xs text-gray-900"
                    value={sortBy}
                    onChange={(event) =>
                      setSortBy(event.target.value as SurfaceProductSort)
                    }
                    aria-label={t("common.sort")}
                  >
                    {SURFACE_PRODUCT_SORT_OPTIONS.map((option) => (
                      <option key={option.value} value={option.value}>
                        {t(option.labelKey)}
                      </option>
                    ))}
                  </select>
                </label>
                {/*
                  Compact chip-style filters (Color / Pattern / Brand), matching
                  the BONI materials chip row look and PaintsMenu2 facets.
                */}
                <div
                  className="flex flex-wrap gap-1"
                  ref={filterMenusRef}
                >
                  <CatalogFilterMenu
                    id="color"
                    label={t("common.color")}
                    count={selectedColorIds.length}
                    open={openFilterMenu === "color"}
                    onToggle={() =>
                      setOpenFilterMenu((current) =>
                        current === "color" ? null : "color",
                      )
                    }
                    loading={categoriesQuery.loading}
                    empty={!categoriesQuery.loading && colorOptions.length === 0}
                    options={colorOptions.map((option) => ({
                      id: option.id,
                      label: option.name,
                    }))}
                    selectedIds={selectedColorIds}
                    onToggleOption={(id) =>
                      setSelectedColorIds((current) => toggleId(current, id))
                    }
                    onClear={() => setSelectedColorIds([])}
                  />
                  {isWallpaperIntent && (
                    <CatalogFilterMenu
                      id="pattern"
                      label={t("common.pattern")}
                      count={selectedPatternIds.length}
                      open={openFilterMenu === "pattern"}
                      onToggle={() =>
                        setOpenFilterMenu((current) =>
                          current === "pattern" ? null : "pattern",
                        )
                      }
                      loading={categoriesQuery.loading}
                      empty={
                        !categoriesQuery.loading && patternOptions.length === 0
                      }
                      options={patternOptions.map((option) => ({
                        id: option.id,
                        label: option.name,
                      }))}
                      selectedIds={selectedPatternIds}
                      onToggleOption={(id) =>
                        setSelectedPatternIds((current) =>
                          toggleId(current, id),
                        )
                      }
                      onClear={() => setSelectedPatternIds([])}
                    />
                  )}
                  <CatalogFilterMenu
                    id="brand"
                    label={t("productLibrary.brands")}
                    count={selectedBrands.length}
                    open={openFilterMenu === "brand"}
                    onToggle={() =>
                      setOpenFilterMenu((current) =>
                        current === "brand" ? null : "brand",
                      )
                    }
                    loading={brandsQuery.loading}
                    empty={!brandsQuery.loading && brandOptions.length === 0}
                    options={brandOptions.map((brand) => ({
                      id: brand,
                      label: brand,
                    }))}
                    selectedIds={selectedBrands}
                    onToggleOption={(id) =>
                      setSelectedBrands((current) => toggleId(current, id))
                    }
                    onClear={() => setSelectedBrands([])}
                  />
                  {hasActiveSurfaceFilters && (
                    <button
                      type="button"
                      className="rounded-sm border border-gray-300 bg-white px-2 py-0.5 text-[11px] text-gray-600 hover:bg-gray-100"
                      onClick={clearSurfaceFilters}
                    >
                      {t("common.clear")}
                    </button>
                  )}
                </div>
              </>
            )}
          </div>
          <div className="min-h-0 flex-1 overflow-y-auto p-3">
            {showPinnedWallpapers && (
              <div className="mb-3">
                <div className="mb-1.5 text-[11px] font-medium text-gray-500">
                  {t("propertyPanel.ncsUsedInPlan")}
                </div>
                <div
                  className="grid grid-cols-2 gap-2"
                  role="listbox"
                  aria-label={t("propertyPanel.wallpaperUsedProducts")}
                >
                  {pinnedWallpapers.map((wallpaper) => (
                    <button
                      key={`pinned-${wallpaper.id}`}
                      type="button"
                      className="group overflow-hidden rounded-sm border border-gray-200 bg-white text-left hover:border-boniYellowPrimary"
                      onClick={() => applyPinnedWallpaper(wallpaper)}
                    >
                      <div className="relative flex aspect-square items-center justify-center bg-gray-100">
                        {wallpaper.imageUrl ? (
                          <img
                            src={wallpaper.imageUrl}
                            alt=""
                            className="h-full w-full object-cover"
                            loading="lazy"
                          />
                        ) : (
                          <span className="text-xs text-gray-400">
                            {t("common.noImage")}
                          </span>
                        )}
                      </div>
                      <div className="min-h-14 px-2 py-1.5">
                        <div className="max-h-8 overflow-hidden text-xs font-medium text-gray-900">
                          {wallpaper.name}
                        </div>
                        {wallpaper.brand && (
                          <div className="truncate text-[11px] text-gray-500">
                            {wallpaper.brand}
                          </div>
                        )}
                      </div>
                    </button>
                  ))}
                </div>
                <div className="mb-1.5 mt-3 text-[11px] font-medium text-gray-500">
                  {t("propertyPanel.wallpaperAllProducts")}
                </div>
              </div>
            )}
            {loading && (
              <div className="py-8 text-center text-sm">
                {t("common.loadingEllipsis")}
              </div>
            )}
            {!loading && items.length === 0 && !showPinnedWallpapers && (
              <div className="py-8 text-center text-sm text-gray-500">
                {emptyMessage}
              </div>
            )}
            <div className="grid grid-cols-2 gap-2">
              {items.map((item: CatalogImageOwner) => {
                const imageUrl = isSurfaceProductBrowse
                  ? resolveSurfaceProductThumbUrl(item)
                  : item.images?.[0];
                const label = getCatalogItemDisplayName(item);
                const asset = assetByProductId.get(item.id);
                const has3DAsset = Boolean(asset);
                const proxyMode = proxyResolver?.(item) ?? "billboardPreview";
                return (
                  <button
                    key={item.id}
                    type="button"
                    className="group overflow-hidden rounded-sm border border-gray-200 bg-white text-left hover:border-boniYellowPrimary"
                    onClick={() => {
                      if (isSurfaceIntent) {
                        onSurfaceSelect?.(item);
                      } else if (isSurfaceProductBrowse) {
                        beginOrCommitSurfaceProduct(item);
                      } else {
                        commitProductSelect(item);
                      }
                    }}
                  >
                    <div className="relative flex aspect-square items-center justify-center bg-gray-100">
                      {imageUrl ? (
                        <img
                          src={imageUrl}
                          alt=""
                          className="h-full w-full object-cover"
                          loading="lazy"
                        />
                      ) : (
                        <span className="text-xs text-gray-400">
                          {t("common.noImage")}
                        </span>
                      )}
                    </div>
                    <div className="min-h-14 px-2 py-1.5">
                      <div className="flex items-start justify-between gap-1">
                        <div className="max-h-8 overflow-hidden text-xs font-medium text-gray-900">
                          {label}
                        </div>
                        {intent === "product" && (
                          <span className="shrink-0 rounded-sm bg-gray-100 px-1 py-0.5 text-[10px] text-gray-600">
                            {has3DAsset
                              ? t("propertyPanel.view3D")
                              : proxyMode === "primitiveProxy"
                                ? t("propertyPanel.proxy")
                                : t("common.preview")}
                          </span>
                        )}
                      </div>
                      {item.brand && (
                        <div className="truncate text-[11px] text-gray-500">
                          {item.brand}
                        </div>
                      )}
                    </div>
                  </button>
                );
              })}
            </div>
            {!loading && items.length > 0 && (
              <div className="mt-3 space-y-2 border-t border-gray-100 pt-3">
                <p className="text-center text-[11px] text-gray-500">
                  {t("common.showingOfTotal", {
                    shown: items.length,
                    total: totalCount,
                  })}
                </p>
                {hasMore && (
                  <button
                    type="button"
                    className="flex h-8 w-full items-center justify-center rounded-sm border border-gray-300 bg-white text-xs font-medium text-gray-800 hover:bg-gray-50 disabled:opacity-60"
                    disabled={loadingMore}
                    onClick={() => {
                      void loadMore();
                    }}
                  >
                    {loadingMore
                      ? t("common.loadingEllipsis")
                      : t("common.loadMore")}
                  </button>
                )}
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
});

/**
 * Compact chip-style multi-select filter menu for catalog browse
 * (Color / Pattern / Brand), matching the BONI materials chip look.
 */
const CatalogFilterMenu: React.FC<{
  id: string;
  label: string;
  count: number;
  open: boolean;
  onToggle: () => void;
  loading: boolean;
  empty: boolean;
  options: Array<{ id: string; label: string }>;
  selectedIds: string[];
  onToggleOption: (id: string) => void;
  onClear: () => void;
}> = ({
  id,
  label,
  count,
  open,
  onToggle,
  loading,
  empty,
  options,
  selectedIds,
  onToggleOption,
  onClear,
}) => {
  const { t } = useTranslation();
  const isActive = count > 0;
  return (
    <div className="relative shrink-0">
      <button
        type="button"
        className={`rounded-sm border px-2 py-0.5 text-[11px] ${
          isActive
            ? "border-gray-800 bg-gray-800 text-white"
            : "border-gray-300 bg-white text-gray-700 hover:bg-gray-100"
        }`}
        onClick={onToggle}
        aria-expanded={open}
        aria-haspopup="listbox"
        aria-controls={`catalog-filter-${id}`}
      >
        {label}
        {count > 0 ? ` (${count})` : ""}
      </button>
      {open && (
        <div
          id={`catalog-filter-${id}`}
          className="absolute left-0 z-10 mt-1 max-h-52 w-52 overflow-y-auto rounded border border-gray-300 bg-white p-1.5 shadow-lg"
          role="listbox"
          aria-label={label}
        >
          {loading && (
            <div className="px-2 py-3 text-center text-[11px] text-gray-500">
              {t("common.loadingEllipsis")}
            </div>
          )}
          {!loading && empty && (
            <div className="px-2 py-3 text-center text-[11px] text-gray-500">
              {t("common.noResults")}
            </div>
          )}
          {options.map((option) => {
            const checked = selectedIds.includes(option.id);
            return (
              <label
                key={option.id}
                className="flex cursor-pointer items-center gap-2 rounded px-1.5 py-1 text-xs text-gray-800 hover:bg-gray-50"
              >
                <input
                  type="checkbox"
                  className="h-3.5 w-3.5"
                  checked={checked}
                  onChange={() => onToggleOption(option.id)}
                />
                <span className="truncate">{option.label}</span>
              </label>
            );
          })}
          {count > 0 && (
            <button
              type="button"
              className="mt-1 w-full rounded-sm px-1.5 py-1 text-left text-[11px] text-gray-500 underline-offset-2 hover:text-gray-800 hover:underline"
              onClick={onClear}
            >
              {t("common.clear")}
            </button>
          )}
        </div>
      )}
    </div>
  );
};
